/**
 * De vanger op de server en zijn meldingen: wat de API en de webapp daarover
 * uitwisselen.
 *
 * De vanger (apps/vanger) is een Chromium met de extensie, op de server. Hij
 * slaapt tot iemand een ronde start - met een tik op de ochtendmelding, of met
 * de knop op de pagina Ronde - en sluit Chromium weer als de ronde klaar is.
 * Verschijnt er onderweg een captcha, dan blijft de ronde wachten en lost een
 * mens hem op via het live beeld op die pagina.
 */
import { z } from 'zod';
import { isoDatumTijdSchema } from './common';

/**
 * Waar de vanger is.
 *
 * - `uit`: de schakelaar staat uit (VANGER_AAN), of deze omgeving heeft geen vanger.
 * - `onbereikbaar`: de schakelaar staat aan, maar de vanger antwoordt niet.
 * - `slaapt`: klaar om te starten; er draait geen Chromium.
 * - `start`: Chromium komt op en de extensie wordt ingesteld.
 * - `bezig`: de ronde loopt.
 * - `captcha`: de ronde wacht op een mens.
 * - `gestopt`: de ronde is afgebroken op een weigering (Access Denied, of een
 *   captcha die niemand oploste). Opnieuw starten mag, maar met een reden.
 */
export const VANGERSTANDEN = [
  'uit',
  'onbereikbaar',
  'slaapt',
  'start',
  'bezig',
  'captcha',
  'gestopt',
] as const;
export const vangerstandSchema = z.enum(VANGERSTANDEN);
export type Vangerstand = z.infer<typeof vangerstandSchema>;

export const VANGERSTAND_LABELS: Record<Vangerstand, string> = {
  uit: 'uit',
  onbereikbaar: 'onbereikbaar',
  slaapt: 'klaar om te starten',
  start: 'start',
  bezig: 'loopt',
  captcha: 'wacht op jou',
  gestopt: 'gestopt',
};

/** Waar de ronde nu is, zoals de extensie het bijhoudt. */
export const vangerVoortgangSchema = z.object({
  /** De hoeveelste zoekopdracht van deze ronde, vanaf 1. */
  index: z.number().int().min(0),
  totaal: z.number().int().min(0),
  naam: z.string().nullable(),
  pagina: z.number().int().min(0),
  /** Unieke woningen die deze zoekopdracht tot nu toe zag. */
  uniek: z.number().int().min(0),
  /** Wat Funda zei dat er in totaal is. */
  fundaTotaal: z.number().int().min(0).nullable(),
});
export type VangerVoortgang = z.infer<typeof vangerVoortgangSchema>;

export const vangerRondeSchema = z.object({
  begonnenOp: isoDatumTijdSchema,
  klaarOp: isoDatumTijdSchema.nullable(),
  /** Eén regel: "11 zoekopdrachten, 4 nieuw, 2 gewijzigd, 0 verdwenen". */
  uitslag: z.string().nullable(),
});
export type VangerRonde = z.infer<typeof vangerRondeSchema>;

/** Wat de webapp over de vanger te zien krijgt. */
export const vangerStandSchema = z.object({
  staat: vangerstandSchema,
  sinds: isoDatumTijdSchema.nullable(),
  voortgang: vangerVoortgangSchema.nullable(),
  /** Sinds wanneer er een captcha wacht. */
  captchaSinds: isoDatumTijdSchema.nullable(),
  /** Eén zin: wat er misging, of waarom hij stopte. */
  melding: z.string().nullable(),
  laatsteRonde: vangerRondeSchema.nullable(),
  /** Of Chromium via de WireGuard-proxy naar buiten gaat. */
  viaProxy: z.boolean(),
  /** Hoe laat de ochtendmelding komt, `08:00`. */
  ochtend: z.string(),
  /** `afroep`: de melding vraagt om een tik. `automatisch`: de ronde start zelf. */
  start: z.enum(['afroep', 'automatisch']),
  /**
   * Het browservenster van de vanger op de NAS (`CAPTCHA_URL`, sinds MA-41
   * `https://vanger.mamimap.nl`), of null als het niet is ingesteld.
   */
  venster: z.url().nullable(),
});
export type VangerStand = z.infer<typeof vangerStandSchema>;

/** Een tik, een veeg of een toets op het live beeld, doorgegeven aan de servertab. */
export const vangerInvoerSchema = z.discriminatedUnion('soort', [
  /** Muisknop omlaag en weer omhoog; x en y in CSS-pixels van de pagina. */
  z.object({
    soort: z.literal('klik'),
    x: z.number().min(0).max(10_000),
    y: z.number().min(0).max(10_000),
  }),
  /** Ingedrukt houden (de knop van een "houd vast"-captcha): omlaag, later omhoog. */
  z.object({
    soort: z.enum(['omlaag', 'omhoog']),
    x: z.number().min(0).max(10_000),
    y: z.number().min(0).max(10_000),
  }),
  z.object({
    soort: z.literal('scroll'),
    x: z.number().min(0).max(10_000),
    y: z.number().min(0).max(10_000),
    dy: z.number().min(-5000).max(5000),
  }),
  z.object({ soort: z.literal('tekst'), tekst: z.string().min(1).max(200) }),
  z.object({ soort: z.literal('toets'), toets: z.enum(['Enter', 'Backspace', 'Tab', 'Escape']) }),
]);
export type VangerInvoer = z.infer<typeof vangerInvoerSchema>;

/** `08:00` - uur en minuut, 24-uursklok. */
export const ochtendtijdSchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Een tijd als 08:00.');

export const vangerInstellingenSchema = z.object({ ochtend: ochtendtijdSchema });
export type VangerInstellingen = z.infer<typeof vangerInstellingenSchema>;

/* --- push ------------------------------------------------------------------ */

/** Wat `PushSubscription.toJSON()` oplevert, en wat de API bewaart. */
export const pushAbonnementSchema = z.object({
  endpoint: z.url().max(2000),
  keys: z.object({
    p256dh: z.string().min(1).max(500),
    auth: z.string().min(1).max(200),
  }),
});
export type PushAbonnementInvoer = z.infer<typeof pushAbonnementSchema>;

export const pushSleutelSchema = z.object({
  /** De publieke VAPID-sleutel, of null als push op deze server niet is ingesteld. */
  sleutel: z.string().nullable(),
});

/** Wat er in een pushbericht zit; de service worker van de webapp leest dit. */
export const pushBerichtSchema = z.object({
  titel: z.string(),
  tekst: z.string(),
  /** Waar een tik op de melding heen gaat, binnen de webapp. */
  url: z.string(),
  /** Meldingen met dezelfde tag vervangen elkaar in plaats van zich op te stapelen. */
  tag: z.string(),
});
export type PushBericht = z.infer<typeof pushBerichtSchema>;

export const pushUitslagSchema = z.object({
  apparaten: z.number().int(),
  verzonden: z.number().int(),
  /** Wat er gebeurde als er geen apparaat was: mail, droogloop, of niets. */
  mail: z.enum(['verzonden', 'droogloop', 'niemand', 'niet-nodig']),
});
export type PushUitslag = z.infer<typeof pushUitslagSchema>;
